// RecordReplyForm — "Record a reply or letter…" on an open ask (RRA v2.1 / W3,
// consensus A3; spec §3.W3.7).
//
// Staff record how a verifier answered OUTSIDE the online form: a reply email, a
// letter, or a document they sent. The answers are the same question set as
// everywhere else (E2): position to know, then each IN-GAP question Yes / No /
// They couldn't say. An optional PDF is stored FIRST through the documents route
// (quarantined: kept, never read), then the answer is recorded and points at it.
// Saving closes the ask, its reminders and any front-line task (server side).
//
// There is NO free-text field: nothing here can hold a diagnosis. "They couldn't
// say" records no evidence and is never a negative.

import { useState } from 'react';

import {
  recordRraReply,
  rraErrorMessage,
  uploadRraStaffDocument,
  type RraStaffDocumentKind,
  type RraVerificationView,
} from '@/lib/rraStaffClient';
import { disabilityQuestion, needQuestion, positionQuestion, SPOKEN_DEFINITION } from '@/lib/rraCopy';
import {
  blankReplyDraft,
  cancelledByWithdrawal,
  pdfFileError,
  REPLY_CHANNEL_OPTIONS,
  replyBody,
  replyError,
  todayYmd,
  type GapFlags,
  type ReplyDraft,
} from '@/lib/rraVerificationUi';
import { ProngAnswer } from './ProngAnswer';

/** The kind a reply's attached file is filed under (the channel it arrived by). */
const DOCUMENT_KIND_FOR_CHANNEL = {
  reply_email: 'reply_email',
  letter: 'letter',
  uploaded_document: 'document',
} as const satisfies Record<'reply_email' | 'letter' | 'uploaded_document', RraStaffDocumentKind>;

const HELP_NO_DIAGNOSIS =
  "Don't record a diagnosis, condition name, or medical detail. Yes, No, or \"They couldn't say\" on each question is all that is kept."; // DRAFT
const HELP_WITHDRAWN =
  'Permission to contact was withdrawn, so a reply received now is noted but never used.'; // DRAFT
const FILE_LABEL = 'Their reply, letter, or document (PDF, optional)'; // DRAFT
const FILE_HELPER = 'It is stored with the case and not read.'; // DRAFT

export function RecordReplyForm({
  caseId,
  verification,
  gap,
  subjectName,
  needObject,
  onRecorded,
  onCancel,
}: {
  caseId: string;
  verification: RraVerificationView;
  /** The in-gap questions this ask covers. */
  gap: GapFlags;
  subjectName: string;
  needObject: string;
  /** Called after the answer is recorded; the page reloads and shows its own notice. */
  onRecorded: (result: { usedAsVerification: boolean }) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<ReplyDraft>(() => blankReplyDraft());
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A stored file survives a failed record step, so a retry never uploads twice.
  const [storedIndex, setStoredIndex] = useState<number | null>(null);

  const withdrawn = cancelledByWithdrawal(verification);
  const idBase = `reply-${verification.verificationId}`;

  async function submit() {
    setError(null);
    const problem = replyError(draft, gap);
    if (problem) return setError(problem);
    if (file && storedIndex === null) {
      const fileProblem = pdfFileError(file);
      if (fileProblem) return setError(fileProblem);
    }
    setBusy(true);
    try {
      let documentIndex = storedIndex;
      if (file && documentIndex === null && draft.channel !== '') {
        const uploaded = await uploadRraStaffDocument(
          caseId,
          file,
          DOCUMENT_KIND_FOR_CHANNEL[draft.channel],
          verification.verificationId,
        );
        documentIndex = uploaded.document.index;
        setStoredIndex(documentIndex);
      }
      const body = replyBody(draft, gap, documentIndex !== null ? { staffDocumentIndex: documentIndex } : {});
      if (!body) return setError(replyError(draft, gap) ?? 'Please complete the form.');
      const res = await recordRraReply(caseId, verification.verificationId, body);
      await onRecorded({ usedAsVerification: res.usedAsVerification });
    } catch (err) {
      setError(rraErrorMessage(err, 'The reply could not be recorded.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ background: 'var(--surface-2, transparent)', marginTop: 'var(--space-2)' }}>
      <div style={{ fontWeight: 'var(--weight-medium)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-2)' }}>
        Record a reply or letter
      </div>
      <div className="alert" data-tone="warning" style={{ marginBottom: 'var(--space-3)' }}>
        <div className="alert-body" style={{ fontSize: 'var(--text-xs)' }}>
          <strong>{HELP_NO_DIAGNOSIS}</strong>
          {withdrawn && <div style={{ marginTop: 'var(--space-1)' }}>{HELP_WITHDRAWN}</div>}
        </div>
      </div>

      <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }} disabled={busy}>
        <legend className="label">How did it come in?</legend>
        <div className="cluster" style={{ gap: 'var(--space-5)' }}>
          {REPLY_CHANNEL_OPTIONS.map((o) => (
            <label key={o.value} className="cluster" style={{ gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name={`${idBase}-channel`}
                checked={draft.channel === o.value}
                onChange={() => setDraft({ ...draft, channel: o.value })}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="field">
        <label className="label" htmlFor={`${idBase}-date`}>When we received it</label>
        <input
          id={`${idBase}-date`}
          type="date"
          className="input"
          max={todayYmd()}
          value={draft.receivedOn}
          disabled={busy}
          onChange={(e) => setDraft({ ...draft, receivedOn: e.target.value })}
        />
      </div>

      <ProngAnswer
        legend={positionQuestion(subjectName)}
        name={`${idBase}-position`}
        cantSay={false}
        disabled={busy}
        value={draft.answers.position}
        onChange={(v) => setDraft({ ...draft, answers: { ...draft.answers, position: v === 'yes' ? 'yes' : 'no' } })}
      />
      {draft.answers.position === 'yes' && gap.askDisability && (
        <ProngAnswer
          legend={disabilityQuestion(subjectName)}
          name={`${idBase}-disability`}
          disabled={busy}
          value={draft.answers.disability}
          onChange={(v) => setDraft({ ...draft, answers: { ...draft.answers, disability: v } })}
          above={<div style={{ color: 'var(--fg-muted)', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-1)' }}>{SPOKEN_DEFINITION}</div>}
        />
      )}
      {draft.answers.position === 'yes' && gap.askNeed && (
        <ProngAnswer
          legend={needQuestion(subjectName, needObject)}
          name={`${idBase}-need`}
          disabled={busy}
          value={draft.answers.need}
          onChange={(v) => setDraft({ ...draft, answers: { ...draft.answers, need: v } })}
        />
      )}

      <div className="field">
        <label className="label" htmlFor={`${idBase}-file`}>{FILE_LABEL}</label>
        <input
          id={`${idBase}-file`}
          type="file"
          className="input"
          accept="application/pdf,.pdf"
          disabled={busy || storedIndex !== null}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <div style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>{FILE_HELPER}</div>
      </div>

      {error && (
        <div className="alert" data-tone="danger" role="alert" style={{ marginBottom: 'var(--space-2)' }}>
          <div className="alert-body">{error}</div>
        </div>
      )}
      <div className="cluster" style={{ gap: 'var(--space-2)' }}>
        <button type="button" className="btn" data-variant="primary" data-size="sm" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Recording…' : 'Record the answer'}
        </button>
        <button type="button" className="btn" data-variant="ghost" data-size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
